import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatusCode,
  DefaultActiveSpeakerPolicy,
} from 'amazon-chime-sdk-js';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, Captions, Languages, Loader2, ShieldCheck, Disc,
  Volume2, VolumeX, SlidersHorizontal, Maximize2, Minimize2, Search, Users,
  ScreenShare, ScreenShareOff, Brain, UserPlus, X, Copy, Check,
} from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';

// Live video visit over the Amazon Chime SDK.
//
// One page serves both sides of the call: staff get transcription controls,
// an "End visit for everyone" button, and transcript autosave; the patient
// gets Join/Leave. Captions come from Chime live transcription (Amazon
// Transcribe); when a final caption's language differs from the viewer's
// chosen caption language it is translated server-side (Amazon Translate)
// and shown beneath the original.
//
// Caption languages the viewer can read in. Codes are Amazon Translate's.
const CAPTION_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
];

// Caption text sizes the viewer can pick in the settings sheet.
const CAP_SIZE = { sm: 'text-[12px]', md: 'text-[14px]', lg: 'text-[17px]', xl: 'text-[20px]' };

function fmtClock(ms) {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

// ExternalUserId is "<users.id>#<display name>" (set server-side at join).
const nameFromExt = (ext) => {
  const i = (ext || '').indexOf('#');
  return i === -1 ? 'Participant' : (ext.slice(i + 1) || 'Participant');
};
const baseLang = (code) => String(code || 'en').slice(0, 2).toLowerCase();

// UI strings for the two screen languages a visitor can pick before the
// lobby (staff/patients always get English — the app itself is English).
// Caption language is a separate, richer choice; this is just the chrome.
const UI_STRINGS = {
  en: {
    videoVisitWith: 'Video visit with',
    videoVisit: 'Video visit',
    ready: 'Ready when you are',
    joiningAs: 'Joining as',
    noAccount: 'no account needed',
    mic: 'Mic', camera: 'Camera', microphone: 'Microphone',
    micHint: "Say something — if this bar doesn't move, pick a different microphone above.",
    captionsIn: 'Show captions in',
    captionsHint: 'Anything spoken in another language is translated for you automatically.',
    join: 'Join visit', connecting: 'Connecting…',
    encrypted: 'Encrypted in transit. If recording is turned on, everyone sees a red badge.',
    waitingOthers: 'Waiting for others to join…',
    waitingParticipant: 'Waiting for the other participant…',
    withNames: 'With',
    liveCaptions: 'Live captions',
    captionsEmpty: 'Captions appear here as people speak.',
    captionsOn: 'Captions on',
    leave: 'Leave',
    recording: 'Recording',
    encryptedShort: 'Encrypted',
    you: 'You', screen: 'Screen',
    ended: 'This visit has ended',
    endedGuest: 'Thanks for joining. You can close this window now.',
    enableSound: 'Tap to turn on sound',
    soundOn: 'Sound is on',
    soundOff: 'No sound? Tap here',
    showCaps: 'Show captions',
    hideCaps: 'Hide captions',
    capsWait: 'Captions start when the host turns them on',
    transcript: 'Transcript',
    backToVideo: 'Back to video',
    searchVisit: 'Search this visit',
    capSettings: 'Caption settings',
    done: 'Done',
    liveCapsSub: "Show what's said as text during the visit",
    textSize: 'Text size',
    translateTo: 'Translate captions to',
    noTranslate: 'No translation',
    background: 'Background',
    solidDark: 'Solid dark',
    highlighted: 'Highlighted',
    speakerNames: 'Show speaker names',
    capsDisclaimer: 'Captions are automatic and may contain errors.',
    autosaves: 'The transcript autosaves to the visit summary.',
    lblSound: 'Sound', lblMute: 'Mute', lblUnmute: 'Unmute', lblCamera: 'Camera', lblCaptions: 'Captions',
    rolePatient: 'Patient', roleGuest: 'Guest', roleCare: 'Care team',
  },
  es: {
    videoVisitWith: 'Videoconsulta con',
    videoVisit: 'Videoconsulta',
    ready: 'Cuando estés listo',
    joiningAs: 'Entrando como',
    noAccount: 'sin necesidad de cuenta',
    mic: 'Micrófono', camera: 'Cámara', microphone: 'Micrófono',
    micHint: 'Di algo — si esta barra no se mueve, elige otro micrófono arriba.',
    captionsIn: 'Mostrar subtítulos en',
    captionsHint: 'Todo lo que se diga en otro idioma se traduce automáticamente para ti.',
    join: 'Entrar a la consulta', connecting: 'Conectando…',
    encrypted: 'Cifrado en tránsito. Si se activa la grabación, todos ven una insignia roja.',
    waitingOthers: 'Esperando a que entren los demás…',
    waitingParticipant: 'Esperando al otro participante…',
    withNames: 'Con',
    liveCaptions: 'Subtítulos en vivo',
    captionsEmpty: 'Los subtítulos aparecerán aquí cuando alguien hable.',
    captionsOn: 'Subtítulos activados',
    leave: 'Salir',
    recording: 'Grabando',
    encryptedShort: 'Cifrado',
    you: 'Tú', screen: 'Pantalla',
    ended: 'Esta consulta ha terminado',
    endedGuest: 'Gracias por participar. Ya puedes cerrar esta ventana.',
    enableSound: 'Toca para activar el sonido',
    soundOn: 'Sonido activado',
    soundOff: '¿Sin sonido? Toca aquí',
    showCaps: 'Mostrar subtítulos',
    hideCaps: 'Ocultar subtítulos',
    capsWait: 'Los subtítulos comienzan cuando el anfitrión los activa',
    transcript: 'Transcripción',
    backToVideo: 'Volver al video',
    searchVisit: 'Buscar en esta consulta',
    capSettings: 'Ajustes de subtítulos',
    done: 'Listo',
    liveCapsSub: 'Muestra lo que se dice como texto durante la consulta',
    textSize: 'Tamaño del texto',
    translateTo: 'Traducir subtítulos a',
    noTranslate: 'Sin traducción',
    background: 'Fondo',
    solidDark: 'Oscuro sólido',
    highlighted: 'Resaltado',
    speakerNames: 'Mostrar nombres',
    capsDisclaimer: 'Los subtítulos son automáticos y pueden contener errores.',
    autosaves: 'La transcripción se guarda automáticamente en el resumen de la consulta.',
    lblSound: 'Sonido', lblMute: 'Silenciar', lblUnmute: 'Activar', lblCamera: 'Cámara', lblCaptions: 'Subtítulos',
    rolePatient: 'Paciente', roleGuest: 'Invitado', roleCare: 'Equipo médico',
  },
};

// Bumped on user-facing telehealth changes; shown tiny in the lobby so a
// phone serving a stale cached bundle is immediately identifiable.
const ROOM_BUILD = 'r-0921c';

// First caption language: whatever this person chose last time, else the
// device's own language (a Spanish-language phone defaults to Español with
// zero taps), else English.
const initialCaptionLang = () => {
  try {
    const saved = localStorage.getItem('ml-caption-lang');
    if (saved && (saved === 'off' || CAPTION_LANGS.some((l) => l.code === saved))) return saved;
  } catch { /* storage blocked */ }
  const nav = baseLang(typeof navigator !== 'undefined' ? navigator.language : 'en');
  return CAPTION_LANGS.some((l) => l.code === nav) ? nav : 'en';
};

export default function VideoVisitPage({ guest = false }) {
  const { sessionId, guestToken } = useParams();
  const navigate = useNavigate();
  const { currentOrg, currentRole } = useOrg();
  const { profile } = useAuth();

  // Guest lobby: who's inviting them (fetched by token, no auth).
  const [guestInfo, setGuestInfo] = useState(null);
  useEffect(() => {
    if (!guest || !guestToken) return;
    api.getGuestVisit(guestToken)
      .then(setGuestInfo)
      .catch((err) => { setError(err.message || 'This visit link is not valid'); });
  }, [guest, guestToken]);

  // 'lobby' → 'connecting' → 'in-call' → 'ended' | 'error'
  const [phase, setPhase] = useState('lobby');
  const [error, setError] = useState('');
  const [role, setRole] = useState(null);

  const [muted, setMuted] = useState(false);
  // Lobby device check: pick a specific mic/camera and SEE the mic working
  // before joining. Without this, join grabbed the first microphone in the
  // device list — on a Mac that's often not the one being spoken into
  // (iPhone Continuity mic, audio interface), which made calls one-way
  // silent with no clue why.
  const [micDevices, setMicDevices] = useState([]);
  const [camDevices, setCamDevices] = useState([]);
  const [micId, setMicId] = useState('');
  const [camId, setCamId] = useState('');
  const [micLevel, setMicLevel] = useState(0);
  const meterRef = useRef(null); // { stream, ctx, timer }
  const [cameraOn, setCameraOn] = useState(true);
  const [tiles, setTiles] = useState([]); // [{tileId, attendeeId, isLocal}]
  const [roster, setRoster] = useState({}); // attendeeId -> name
  const [rosterExt, setRosterExt] = useState({}); // attendeeId -> externalUserId
  const [speakerIds, setSpeakerIds] = useState([]); // active speakers, loudest first
  const [mutedMap, setMutedMap] = useState({}); // attendeeId -> muted
  const sessionClientIdRef = useRef(null);

  const [captionsOn, setCaptionsOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false);
  const [captionBusy, setCaptionBusy] = useState(false);
  const [myLang, setMyLang] = useState(initialCaptionLang);
  const [captions, setCaptions] = useState([]); // [{id, speaker, text, lang, partial, translated}]
  // Screen language for the guest chrome — visitors pick English/Español on
  // an entry screen before the lobby; everyone else gets English.
  const [uiLang, setUiLang] = useState(guest ? null : 'en');
  const t = UI_STRINGS[uiLang || 'en'];
  const [sharing, setSharing] = useState(false);
  const canShare = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  const [inviteOpen, setInviteOpen] = useState(false);
  // The self-preview mirrors the camera's true aspect ratio (portrait on a
  // phone), so what you see is the same framing everyone else gets.
  const [localAspect, setLocalAspect] = useState(16 / 9);
  // Autoplay rescue: if the browser still refuses playback after our
  // gesture unlock (iOS is inventive), surface a tap target — a direct
  // user-gesture play() on the element cannot be refused.
  const [needsAudioTap, setNeedsAudioTap] = useState(false);
  // Speaker-button state: anything that means "the remote mix is not
  // audibly playing" — element paused, or the real bind never landed.
  const [audioBlocked, setAudioBlocked] = useState(false);
  // Panel visibility: 'auto' follows the host's transcription state,
  // 'open' forces the panel (shows a waiting note if transcription is off
  // — tapping CC must always visibly do something), 'hidden' closes it.
  const [capsPanel, setCapsPanel] = useState('auto');
  // Mobile pane: the call itself, or the full searchable transcript.
  const [mobilePane, setMobilePane] = useState('call');
  const [capSettingsOpen, setCapSettingsOpen] = useState(false);
  const [tSearch, setTSearch] = useState('');
  // Viewer caption preferences (text size / background / speaker names),
  // persisted per browser.
  const [capPrefs, setCapPrefs] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('ml-caption-prefs') || '{}');
      return { size: saved.size || 'md', bg: saved.bg || 'solid', names: saved.names !== false };
    } catch { return { size: 'md', bg: 'solid', names: true }; }
  });
  useEffect(() => {
    try { localStorage.setItem('ml-caption-prefs', JSON.stringify(capPrefs)); } catch { /* storage blocked */ }
  }, [capPrefs]);
  // Call clock — starts when the call connects.
  const callStartRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);

  const meetingRef = useRef(null);       // DefaultMeetingSession
  const audioElRef = useRef(null);
  const videoElsRef = useRef({});        // tileId -> <video>
  const myLangRef = useRef(initialCaptionLang());
  const rosterRef = useRef({});
  const pendingSaveRef = useRef([]);     // finals awaiting persistence (staff)
  const roleRef = useRef(null);
  const endedByMeRef = useRef(false);
  const captionsBoxRef = useRef(null);

  useEffect(() => {
    myLangRef.current = myLang;
    try { localStorage.setItem('ml-caption-lang', myLang); } catch { /* storage blocked */ }
  }, [myLang]);

  const isStaff = role === 'staff';

  // ── Caption handling ──────────────────────────────────────────────
  const translateIfNeeded = useCallback(async (caption) => {
    if (myLangRef.current === 'off' || baseLang(caption.lang) === myLangRef.current) return;
    try {
      const payload = { text: caption.text, source: caption.lang, target: myLangRef.current };
      const { text } = guest
        ? await api.translateGuestCaption(guestToken, payload)
        : await api.translateCaption(currentOrg.id, payload);
      setCaptions((prev) => prev.map((c) => (c.id === caption.id ? { ...c, translated: text } : c)));
    } catch { /* captions still show in the spoken language */ }
  }, [currentOrg?.id, guest, guestToken]);

  // Switching caption language retranslates what's already on screen —
  // without this, only lines spoken AFTER the switch get translated, which
  // reads as "translation doesn't work" if nobody happens to speak next.
  const langSwitchedRef = useRef(false);
  useEffect(() => {
    if (!langSwitchedRef.current) { langSwitchedRef.current = true; return; } // skip initial mount
    setCaptions((prev) => {
      // Re-run translation for the most recent finished lines (bounded so a
      // long feed doesn't burst-fire dozens of Translate calls).
      prev.slice(-15).forEach((c) => { if (!c.partial) translateIfNeeded(c); });
      return prev.map((c) => ({ ...c, translated: null }));
    });
  }, [myLang, translateIfNeeded]);

  // ── Debug mode (?debug=1 on the room URL): live on-screen readout of the
  // audio element, its stream tracks, per-speaker volumes from Chime's
  // signaling, and transcript-event counts — so a silent phone tells us
  // WHERE the chain breaks instead of us guessing.
  const debugMode = typeof window !== 'undefined' && window.location.search.includes('debug');
  const transcriptCountRef = useRef(0);
  const volumesRef = useRef({}); // attendeeId -> last volume 0..1
  const [debugInfo, setDebugInfo] = useState(null);

  const onTranscriptEvent = useCallback((event) => {
    transcriptCountRef.current += 1;
    if (!event?.results) {
      // TranscriptionStatus event (no text): this is how non-staff
      // participants learn captions were toggled mid-call — their
      // captionsOn otherwise only reflects the state at join time.
      const status = String(event?.type ?? '').toLowerCase();
      if (status === 'started' || status === 'resumed') setCaptionsOn(true);
      else if (status === 'stopped' || status === 'failed') setCaptionsOn(false);
      return;
    }
    // Captions arriving at all means transcription is running — belt and
    // braces for a missed status event (React bails out on same-value sets).
    setCaptionsOn(true);
    for (const result of event.results) {
      const alt = result.alternatives?.[0];
      if (!alt?.transcript) continue;
      const attendee = alt.items?.find((it) => it.attendee)?.attendee;
      const speaker = nameFromExt(attendee?.externalUserId);
      const caption = {
        id: result.resultId,
        speaker,
        extUserId: attendee?.externalUserId || null,
        text: alt.transcript,
        lang: result.languageCode || 'en-US',
        partial: !!result.isPartial,
        translated: null,
        at: Date.now(),
      };
      setCaptions((prev) => {
        const i = prev.findIndex((c) => c.id === caption.id);
        const next = i === -1 ? [...prev, caption] : prev.map((c, j) => (j === i ? { ...c, ...caption } : c));
        return next.slice(-60); // keep the feed bounded
      });
      if (!caption.partial) {
        translateIfNeeded(caption);
        if (roleRef.current === 'staff') {
          pendingSaveRef.current.push({
            result_id: caption.id,
            speaker_name: speaker,
            ext_user_id: caption.extUserId,
            content: caption.text,
            language_code: caption.lang,
            spoken_at: new Date().toISOString(),
          });
        }
      }
    }
  }, [translateIfNeeded]);

  // Transcript autosave (staff): flush finals every 8s and on teardown.
  const flushTranscript = useCallback(async () => {
    if (roleRef.current !== 'staff' || pendingSaveRef.current.length === 0) return;
    const batch = pendingSaveRef.current.splice(0, 200);
    try {
      await api.saveTelehealthUtterances(currentOrg.id, sessionId, batch);
    } catch {
      pendingSaveRef.current.unshift(...batch); // retry on next tick
    }
  }, [currentOrg?.id, sessionId]);

  useEffect(() => {
    const t = setInterval(flushTranscript, 8000);
    return () => clearInterval(t);
  }, [flushTranscript]);

  // Keep the caption feed pinned to the newest line.
  useEffect(() => {
    const el = captionsBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [captions]);

  // ── Lobby mic meter ───────────────────────────────────────────────
  const stopMeter = useCallback(() => {
    const m = meterRef.current;
    if (!m) return;
    clearInterval(m.timer);
    try { m.ctx.close(); } catch { /* already closed */ }
    m.stream?.getTracks().forEach((t) => t.stop());
    meterRef.current = null;
  }, []);

  useEffect(() => {
    if (phase !== 'lobby') { stopMeter(); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: micId ? { deviceId: { exact: micId } } : true,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        stopMeter();
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const timer = setInterval(() => {
          analyser.getByteTimeDomainData(buf);
          let peak = 0;
          for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i] - 128));
          setMicLevel(Math.min(100, Math.round((peak / 128) * 160)));
        }, 100);
        meterRef.current = { stream, ctx, timer };
        // Labels only populate after permission is granted.
        const devs = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) {
          setMicDevices(devs.filter((d) => d.kind === 'audioinput' && d.deviceId));
          setCamDevices(devs.filter((d) => d.kind === 'videoinput' && d.deviceId));
        }
      } catch { /* mic denied — joining listen-only still works */ }
    })();
    return () => { cancelled = true; stopMeter(); };
  }, [phase, micId, stopMeter]);

  // ── Join ──────────────────────────────────────────────────────────
  const join = useCallback(async () => {
    // iOS unlock — MUST run synchronously inside the tap, before any await:
    // a play() attempt in the gesture's call stack marks the element
    // user-activated, so Chime's bind (after network calls) may start audio.
    const audioEl = audioElRef.current;
    if (audioEl) {
      try {
        if (!audioEl.srcObject) audioEl.srcObject = new MediaStream();
        audioEl.play().catch(() => { /* pending until real audio arrives */ });
      } catch { /* older browsers without srcObject setter */ }
    }
    setPhase('connecting');
    setError('');
    stopMeter(); // release the lobby's mic stream before Chime claims the device
    try {
      const data = guest
        ? await api.joinGuestVisit(guestToken)
        : await api.joinTelehealthSession(currentOrg.id, sessionId);
      setRole(data.role);
      roleRef.current = data.role;
      setCaptionsOn(!!data.session.transcription_on);
      setRecording(data.session.recording_status === 'recording');
      sessionClientIdRef.current = data.session.client_id || null;

      const logger = new ConsoleLogger('medilink-visit', LogLevel.WARN);
      const deviceController = new DefaultDeviceController(logger);
      const config = new MeetingSessionConfiguration(data.meeting, data.attendee);
      const meetingSession = new DefaultMeetingSession(config, logger, deviceController);
      meetingRef.current = meetingSession;
      const av = meetingSession.audioVideo;

      // Devices: default mic + camera. A visit UI is not the place for an
      // AV-settings maze; browsers surface their own device pickers.
      // Use the mic checked in the lobby; otherwise prefer the system
      // default over whatever happens to be first in the list.
      const mics = await av.listAudioInputDevices();
      const micPick = (micId && mics.find((m) => m.deviceId === micId)?.deviceId)
        || mics.find((m) => m.deviceId === 'default')?.deviceId
        || mics[0]?.deviceId;
      if (micPick) await av.startAudioInput(micPick);
      try {
        const speakers = await av.listAudioOutputDevices();
        if (speakers.length) await av.chooseAudioOutput(speakers[0].deviceId);
      } catch { /* Safari has no output selection */ }
      if (audioElRef.current) av.bindAudioElement(audioElRef.current);

      av.addObserver({
        videoTileDidUpdate: (tile) => {
          if (!tile.tileId || (!tile.boundAttendeeId && !tile.localTile)) return;
          setTiles((prev) => {
            const rest = prev.filter((tl) => tl.tileId !== tile.tileId);
            return [...rest, {
              tileId: tile.tileId,
              attendeeId: tile.boundAttendeeId,
              isLocal: !!tile.localTile,
              isContent: !!tile.isContent,
            }];
          });
          const el = videoElsRef.current[tile.tileId];
          if (el) av.bindVideoElement(tile.tileId, el);
        },
        videoTileWasRemoved: (tileId) => {
          setTiles((prev) => prev.filter((t) => t.tileId !== tileId));
          delete videoElsRef.current[tileId];
        },
        audioVideoDidStop: (status) => {
          const code = status.statusCode();
          if (endedByMeRef.current) return;
          if (code === MeetingSessionStatusCode.MeetingEnded) {
            setPhase('ended');
          } else if (code !== MeetingSessionStatusCode.Left) {
            setError('Connection lost. Rejoin when you are ready.');
            setPhase('lobby');
          }
        },
      });

      av.realtimeSubscribeToAttendeeIdPresence((attendeeId, present, externalUserId) => {
        // The content-share attendee ("<id>#content") is a screen, not a person.
        if (String(attendeeId).endsWith('#content')) return;
        if (present) {
          // Speaker volumes arrive over signaling (no WebAudio needed) —
          // in debug mode this proves whether audio is decoding at all.
          try {
            av.realtimeSubscribeToVolumeIndicator(attendeeId, (id, volume, muted) => {
              volumesRef.current[id] = volume;
              if (muted !== null && muted !== undefined) {
                setMutedMap((m) => (m[id] === muted ? m : { ...m, [id]: muted }));
              }
            });
          } catch { /* older SDK */ }
        }
        setRoster((prev) => {
          const next = { ...prev };
          if (present) next[attendeeId] = nameFromExt(externalUserId);
          else delete next[attendeeId];
          rosterRef.current = next;
          return next;
        });
        setRosterExt((prev) => {
          const next = { ...prev };
          if (present) next[attendeeId] = externalUserId || '';
          else delete next[attendeeId];
          return next;
        });
      });

      // Keep the share button honest when the browser or another share
      // stops it (user hits the browser's own "Stop sharing" bar, etc).
      av.addContentShareObserver?.({ contentShareDidStop: () => setSharing(false) });

      av.transcriptionController?.subscribeToTranscriptEvent(onTranscriptEvent);

      // Active-speaker highlight for the group grid.
      try {
        av.subscribeToActiveSpeakerDetector(new DefaultActiveSpeakerPolicy(), (ids) => setSpeakerIds(ids || []));
      } catch { /* detector unavailable on this SDK build */ }

      av.start();
      try {
        const cams = await av.listVideoInputDevices();
        const camPick = (camId && cams.find((c) => c.deviceId === camId)?.deviceId)
          || cams[0]?.deviceId;
        if (camPick && cameraOn) {
          await av.startVideoInput(camPick);
          av.startLocalVideoTile();
        }
      } catch { setCameraOn(false); }
      if (muted) av.realtimeMuteLocalAudio();

      setPhase('in-call');
    } catch (err) {
      if (err.status === 410) { setPhase('ended'); return; }
      setError(err.message || 'Could not join the visit.');
      setPhase('lobby');
    }
  }, [currentOrg?.id, sessionId, guest, guestToken, onTranscriptEvent, cameraOn, muted, micId, camId, stopMeter]);

  // ONE persistent audio element for remote audio, created on mount rather
  // than rendered with the in-call view. iOS only lets an element play if
  // play() was attempted inside a user tap — an element that first exists
  // AFTER the async join resolves can never be unlocked, which made phones
  // silent. join() unlocks this element synchronously inside the tap.
  useEffect(() => {
    const el = document.createElement('audio');
    el.autoplay = true;
    el.setAttribute('playsinline', '');
    el.addEventListener('playing', () => { setNeedsAudioTap(false); setAudioBlocked(false); });
    document.body.appendChild(el);
    audioElRef.current = el;
    return () => {
      try { el.pause(); } catch { /* already stopped */ }
      el.remove();
      if (audioElRef.current === el) audioElRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'in-call') return undefined;
    if (!callStartRef.current) callStartRef.current = Date.now();
    const tick = setInterval(() => setElapsed(Date.now() - callStartRef.current), 1000);
    return () => clearInterval(tick);
  }, [phase]);

  // Belt-and-braces rebind once the in-call UI is up (covers reconnects),
  // plus a watchdog: while in-call, a bound-but-paused element means the
  // browser blocked autoplay — show the tap-for-sound rescue.
  useEffect(() => {
    if (phase !== 'in-call') return undefined;
    const av = meetingRef.current?.audioVideo;
    const el = audioElRef.current;
    if (av && el) {
      try { av.bindAudioElement(el); el.play().catch(() => {}); } catch { /* no output device */ }
    }
    const watchdog = setInterval(() => {
      const a = audioElRef.current;
      if (!a) return;
      const tracks = a.srcObject?.getAudioTracks ? a.srcObject.getAudioTracks() : [];
      if (tracks.length === 0) {
        // Element still holds the empty unlock stream — the real bind never
        // landed (or failed silently). Re-bind rather than sit silent.
        const avNow = meetingRef.current?.audioVideo;
        if (avNow) { try { avNow.bindAudioElement(a); } catch { /* retry next tick */ } }
        setAudioBlocked(true);
        return;
      }
      if (a.paused) { setNeedsAudioTap(true); setAudioBlocked(true); }
      else setAudioBlocked(false);
    }, 2000);
    return () => clearInterval(watchdog);
  }, [phase]);

  // Debug readout refresh (only with ?debug=1 in the URL).
  useEffect(() => {
    if (!debugMode || phase !== 'in-call') return undefined;
    const t = setInterval(() => {
      const el = audioElRef.current;
      const tracks = el?.srcObject?.getAudioTracks ? el.srcObject.getAudioTracks() : [];
      setDebugInfo({
        el: el ? `paused=${el.paused} muted=${el.muted} vol=${el.volume} ready=${el.readyState}` : 'NO ELEMENT',
        tracks: tracks.length
          ? tracks.map((tr) => `${tr.readyState}${tr.muted ? ' MUTED' : ''}${tr.enabled ? '' : ' DISABLED'}`).join(' | ')
          : 'NO AUDIO TRACKS',
        captions: `on=${captionsOn} events=${transcriptCountRef.current}`,
        vols: Object.entries(volumesRef.current)
          .map(([id, v]) => `${(rosterRef.current[id] || 'me').split(' ')[0]}=${v == null ? '?' : Number(v).toFixed(2)}`)
          .join(' ') || 'no volume data',
      });
    }, 1000);
    return () => clearInterval(t);
  }, [debugMode, phase, captionsOn]);

  // Bind any video element that mounted after its tile event arrived.
  useEffect(() => {
    const av = meetingRef.current?.audioVideo;
    if (!av) return;
    for (const t of tiles) {
      const el = videoElsRef.current[t.tileId];
      if (el) { try { av.bindVideoElement(t.tileId, el); } catch { /* tile gone */ } }
    }
  }, [tiles]);

  const leaveMedia = useCallback(() => {
    const av = meetingRef.current?.audioVideo;
    try { av?.stop(); } catch { /* already stopped */ }
    try { av?.stopVideoInput?.(); } catch { /* noop */ }
    meetingRef.current = null;
  }, []);

  useEffect(() => () => { leaveMedia(); flushTranscript(); }, [leaveMedia, flushTranscript]);

  // ── Controls ──────────────────────────────────────────────────────
  const toggleMute = () => {
    const av = meetingRef.current?.audioVideo;
    if (!av) { setMuted((m) => !m); return; }
    if (muted) av.realtimeUnmuteLocalAudio(); else av.realtimeMuteLocalAudio();
    setMuted(!muted);
  };

  const toggleCamera = async () => {
    const av = meetingRef.current?.audioVideo;
    if (!av) { setCameraOn((c) => !c); return; }
    try {
      if (cameraOn) {
        av.stopLocalVideoTile();
        await av.stopVideoInput();
        setCameraOn(false);
      } else {
        const cams = await av.listVideoInputDevices();
        if (cams.length) {
          await av.startVideoInput(cams[0].deviceId);
          av.startLocalVideoTile();
          setCameraOn(true);
        }
      }
    } catch { /* camera unavailable */ }
  };

  const toggleCaptions = async () => {
    if (!isStaff || captionBusy) return;
    setCaptionBusy(true);
    try {
      await api.setTelehealthTranscription(currentOrg.id, sessionId, { on: !captionsOn });
      setCaptionsOn(!captionsOn);
      if (!captionsOn) setCapsPanel('auto');
    } catch (err) {
      setError(err.message || 'Could not toggle captions');
    } finally {
      setCaptionBusy(false);
    }
  };

  const toggleRecording = async () => {
    if (!isStaff || recordingBusy) return;
    setRecordingBusy(true);
    try {
      const res = await api.setTelehealthRecording(currentOrg.id, sessionId, !recording);
      setRecording(res.recording_status === 'recording');
    } catch (err) {
      setError(err.message || 'Could not update recording');
    } finally {
      setRecordingBusy(false);
    }
  };

  const toggleShare = async () => {
    const av = meetingRef.current?.audioVideo;
    if (!av) return;
    try {
      if (sharing) {
        await av.stopContentShare();
        setSharing(false);
      } else {
        await av.startContentShareFromScreenCapture();
        setSharing(true);
      }
    } catch { /* user dismissed the browser's screen picker */ }
  };

  const hangUp = async () => {
    endedByMeRef.current = true;
    if (isStaff) {
      try { await flushTranscript(); } catch { /* best effort */ }
      try { await api.endTelehealthSession(currentOrg.id, sessionId); } catch { /* meeting may be gone */ }
    }
    leaveMedia();
    // A guest has nowhere in the app to go back to — the ended screen
    // tells them they can close the tab.
    if (guest) { setPhase('ended'); return; }
    navigate(currentRole === 'client' ? '/client' : '/app/scheduling');
  };

  // ── Render ────────────────────────────────────────────────────────
  const contentTile = tiles.find((tl) => tl.isContent);
  const remoteTiles = tiles.filter((tl) => !tl.isLocal && !tl.isContent);
  const localTile = tiles.find((tl) => tl.isLocal && !tl.isContent);
  const others = Object.entries(roster).filter(
    ([id]) => id !== meetingRef.current?.configuration?.credentials?.attendeeId,
  );

  // Visitors pick their screen language before anything else — the next
  // screen (mic check, captions, join) then reads in that language.
  if (guest && !uiLang) {
    return (
      <Shell>
        <div className="text-center max-w-sm mx-auto">
          <div className="w-[52px] h-[52px] mx-auto mb-4 rounded-[14px] gradient-logo flex items-center justify-center text-white font-extrabold shadow-glow">
            ML
          </div>
          <h2 className="text-[18px] font-bold text-primary-c mb-1">Choose your language</h2>
          <p className="text-[13px] text-muted-c mb-6">Elige tu idioma</p>
          <div className="space-y-3">
            <button
              onClick={() => { setUiLang('en'); setMyLang('en'); }}
              className="w-full py-[13px] rounded-[10px] text-[14px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
            >
              English
            </button>
            <button
              onClick={() => { setUiLang('es'); setMyLang('es'); }}
              className="w-full py-[13px] rounded-[10px] text-[14px] font-semibold text-primary-c bg-tertiary border border-strong hover:bg-hover-surface transition-all"
            >
              Español
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  if (phase === 'ended') {
    return (
      <Shell>
        <div className="text-center max-w-sm mx-auto">
          <div className="w-[52px] h-[52px] mx-auto mb-4 rounded-full bg-accent-glow flex items-center justify-center">
            <PhoneOff size={22} className="text-accent-light" />
          </div>
          <h2 className="text-[18px] font-bold text-primary-c mb-2">{t.ended}</h2>
          <p className="text-[13px] text-muted-c mb-6">
            {guest ? t.endedGuest : 'Thanks for using MediLink video visits.'}
          </p>
          {!guest && (
            <button
              onClick={() => navigate(currentRole === 'client' ? '/client' : '/app/scheduling')}
              className="px-5 py-[10px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow"
            >
              Back to {currentRole === 'client' ? 'my appointments' : 'scheduling'}
            </button>
          )}
        </div>
      </Shell>
    );
  }

  if (phase === 'lobby' || phase === 'connecting') {
    return (
      <Shell>
        <div className="text-center max-w-sm mx-auto">
          <div className="w-[52px] h-[52px] mx-auto mb-4 rounded-[14px] gradient-logo flex items-center justify-center text-white font-extrabold shadow-glow">
            ML
          </div>
          <h2 className="text-[18px] font-bold text-primary-c mb-1">
            {guest && guestInfo?.org_name ? `${t.videoVisitWith} ${guestInfo.org_name}` : t.videoVisit}
          </h2>
          <p className="text-[13px] text-muted-c mb-5">
            {guest
              ? (guestInfo?.guest_name ? `${t.joiningAs} ${guestInfo.guest_name} — ${t.noAccount}` : t.ready)
              : (profile?.first_name ? `${t.joiningAs} ${profile.first_name} ${profile.last_name || ''}`.trim() : t.ready)}
          </p>
          {error && (
            <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3 py-2 mb-4">{error}</div>
          )}
          <div className="flex items-center justify-center gap-3 mb-4">
            <ToggleChip active={!muted} onClick={() => setMuted((m) => !m)} onIcon={<Mic size={15} />} offIcon={<MicOff size={15} />} label={t.mic} />
            <ToggleChip active={cameraOn} onClick={() => setCameraOn((c) => !c)} onIcon={<Video size={15} />} offIcon={<VideoOff size={15} />} label={t.camera} />
          </div>

          {/* Caption language — asked up front so a Spanish-speaking patient
              and an English-speaking doctor each just confirm their own. */}
          <div className="text-left mb-4">
            <label className="block text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1 flex items-center gap-1">
              <Languages size={11} /> {t.captionsIn}
            </label>
            <select
              value={myLang}
              onChange={(e) => setMyLang(e.target.value)}
              className="w-full px-3 py-[9px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] focus:outline-none focus:border-[#0DA7CA]"
            >
              <option value="off">{t.noTranslate}</option>
              {CAPTION_LANGS.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-muted-c mt-1">
              {t.captionsHint}
            </p>
          </div>

          {micDevices.length > 0 && (
            <div className="text-left space-y-3 mb-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1">{t.microphone}</label>
                <select
                  value={micId}
                  onChange={(e) => setMicId(e.target.value)}
                  className="w-full px-3 py-[9px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[12px] focus:outline-none focus:border-[#0DA7CA]"
                >
                  {micDevices.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
                  ))}
                </select>
                {/* Live level: proof the selected mic is picking you up */}
                <div className="h-[5px] rounded-full bg-tertiary mt-2 overflow-hidden">
                  <div
                    className="h-full rounded-full gradient-accent transition-[width] duration-100"
                    style={{ width: `${micLevel}%` }}
                  />
                </div>
                <p className="text-[10px] text-muted-c mt-1">
                  {t.micHint}
                </p>
              </div>
              {camDevices.length > 1 && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1">{t.camera}</label>
                  <select
                    value={camId}
                    onChange={(e) => setCamId(e.target.value)}
                    className="w-full px-3 py-[9px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[12px] focus:outline-none focus:border-[#0DA7CA]"
                  >
                    {camDevices.map((d, i) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
          <button
            onClick={join}
            disabled={phase === 'connecting'}
            className="w-full py-[12px] rounded-[10px] text-[14px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
          >
            {phase === 'connecting' ? (<><Loader2 size={16} className="animate-spin" /> {t.connecting}</>) : t.join}
          </button>
          <p className="text-[11px] text-muted-c mt-4 flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} /> {t.encrypted}
          </p>
          <p className="text-[9px] text-muted-c opacity-50 mt-2">{ROOM_BUILD}</p>
        </div>
      </Shell>
    );
  }

  // Self-preview sizing: phones get a compact PiP (a 210px-tall preview
  // dominated the screen); desktop keeps the larger one.
  const pipPortrait = typeof window !== 'undefined' && window.innerWidth < 768 ? 118 : 210;
  const pipLandscape = typeof window !== 'undefined' && window.innerWidth < 768 ? 104 : 168;
  const myAttendeeId = meetingRef.current?.configuration?.credentials?.attendeeId || null;
  // Role subtitle for a tile, derived from the attendee's ExternalUserId:
  // guests carry a "guest-" prefix, the session's client is the patient,
  // anyone else joined through the org — the care team.
  const roleFor = (attendeeId) => {
    const ext = rosterExt[attendeeId] || '';
    if (ext.startsWith('guest-')) return t.roleGuest;
    if (sessionClientIdRef.current && ext.split('#')[0] === sessionClientIdRef.current) return t.rolePatient;
    return t.roleCare;
  };
  const capsVisibleNow = capsPanel === 'open' || (capsPanel === 'auto' && captionsOn);
  const langLabel = myLang === 'off' ? t.noTranslate : (CAPTION_LANGS.find((l) => l.code === myLang)?.label || 'English');
  const lastFinal = [...captions].reverse().find((c) => !c.partial) || null;
  const finalCaptions = captions.filter((c) => !c.partial);
  const tq = tSearch.trim().toLowerCase();
  const transcriptRows = tq
    ? finalCaptions.filter((c) =>
        (c.text || '').toLowerCase().includes(tq)
        || (c.translated || '').toLowerCase().includes(tq)
        || (c.speaker || '').toLowerCase().includes(tq))
    : finalCaptions;

  return (
    <div className="fixed inset-0 bg-primary flex flex-col">

      <div className="flex items-center justify-between px-4 py-2.5 border-b border-theme bg-elevated">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[13px] font-semibold text-primary-c">Video visit</span>
          <span className="text-[12px] text-muted-c">
            {others.length === 0 ? t.waitingOthers : `${t.withNames} ${others.map(([, n]) => n).join(', ')}`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-[12px] text-muted-c">
            <Users size={13} /> {Math.max(Object.keys(roster).length, 1)}
          </span>
          <span className="text-[12px] font-mono text-muted-c tabular-nums">{fmtClock(elapsed)}</span>
          {recording && (
            <span className="text-[11px] font-semibold text-red-400 bg-red-500/10 border border-red-500/25 rounded-full px-2.5 py-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {t.recording}
            </span>
          )}
          <span className="text-[11px] text-muted-c flex items-center gap-1"><ShieldCheck size={12} /> {t.encryptedShort}</span>
        </div>
      </div>

      {debugMode && debugInfo && (
        <div className="absolute bottom-20 left-2 z-50 bg-black/85 text-emerald-300 rounded-lg p-2.5 text-[10px] font-mono leading-relaxed max-w-[92vw] break-all">
          <div>AUDIO {debugInfo.el}</div>
          <div>TRACKS {debugInfo.tracks}</div>
          <div>CAPTIONS {debugInfo.captions}</div>
          <div>VOL {debugInfo.vols}</div>
        </div>
      )}

      {/* In-call errors were previously invisible — a failed caption
          toggle looked like a dead button. Dismissable toast. */}
      {error && (
        <button
          onClick={() => setError('')}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-40 max-w-[92vw] px-4 py-2 rounded-[10px] text-[12px] font-semibold text-red-300 bg-black/85 border border-red-500/40 text-left"
        >
          {error} — tap to dismiss
        </button>
      )}

      {needsAudioTap && (
        <button
          onClick={() => {
            const el = audioElRef.current;
            const av = meetingRef.current?.audioVideo;
            if (!el) return;
            try { if (av) av.bindAudioElement(el); } catch { /* play() may still work */ }
            el.muted = false;
            el.volume = 1;
            el.play().then(() => { setNeedsAudioTap(false); setAudioBlocked(false); }).catch(() => {});
          }}
          className="absolute top-14 left-1/2 -translate-x-1/2 z-40 px-5 py-[10px] rounded-full text-[13px] font-semibold text-white gradient-accent shadow-glow-lg animate-fade-in flex items-center gap-2"
        >
          <Volume2 size={14} /> {t.enableSound}
        </button>
      )}

      <div className="flex-1 flex min-h-0 relative">
        {/* Video area */}
        <div className="flex-1 relative p-3 min-w-0">
          {contentTile ? (
            // A shared screen takes the stage; cameras shrink to a strip.
            <div className="w-full h-full flex flex-col gap-3">
              <div className="relative flex-1 min-h-0 rounded-[14px] overflow-hidden bg-black border border-theme">
                <video
                  ref={(el) => { if (el) videoElsRef.current[contentTile.tileId] = el; }}
                  className="w-full h-full object-contain"
                  autoPlay
                  playsInline
                />
                <span className="absolute bottom-2 left-2 text-[11px] font-medium text-white bg-black/50 rounded-md px-2 py-0.5 flex items-center gap-1">
                  <ScreenShare size={11} /> {t.screen}
                </span>
              </div>
              {remoteTiles.length > 0 && (
                <div className="h-[110px] flex gap-3 shrink-0">
                  {remoteTiles.map((tile) => (
                    <div key={tile.tileId} className="relative aspect-video h-full rounded-[10px] overflow-hidden bg-tertiary border border-theme">
                      <video
                        ref={(el) => { if (el) videoElsRef.current[tile.tileId] = el; }}
                        className="w-full h-full object-cover"
                        autoPlay
                        playsInline
                      />
                      <span className="absolute bottom-1 left-1.5 text-[10px] font-medium text-white bg-black/50 rounded px-1.5 py-0.5">
                        {roster[tile.attendeeId] || 'Participant'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : remoteTiles.length === 0 ? (
            <div className="w-full h-full rounded-[14px] bg-elevated border border-theme flex items-center justify-center">
              <div className="text-center">
                <Loader2 size={20} className="animate-spin text-muted-c mx-auto mb-2" />
                <p className="text-[13px] text-muted-c">{t.waitingParticipant}</p>
              </div>
            </div>
          ) : (
            <div className={`w-full h-full grid gap-2 ${remoteTiles.length > 1 ? 'grid-cols-2 auto-rows-fr' : 'grid-cols-1'}`}>
              {remoteTiles.map((tile) => {
                const speaking = speakerIds[0] === tile.attendeeId;
                return (
                  <div
                    key={tile.tileId}
                    className={`relative rounded-[14px] overflow-hidden bg-tertiary border transition-shadow ${
                      speaking ? 'border-[#0DA7CA] ring-2 ring-[#0DA7CA]/50' : 'border-theme'}`}
                  >
                    {/* object-contain, not cover: a phone streams portrait video,
                        and cover was cropping it into a wide tile — which read
                        as "zoomed way in." Letterboxing shows the real frame. */}
                    <video
                      ref={(el) => { if (el) videoElsRef.current[tile.tileId] = el; }}
                      className="w-full h-full object-contain"
                      autoPlay
                      playsInline
                    />
                    <span className="absolute bottom-2 left-2 max-w-[85%] bg-black/55 rounded-lg px-2 py-1 flex items-center gap-1.5">
                      <span className="min-w-0">
                        <span className="block text-[11px] font-semibold text-white leading-tight truncate">
                          {roster[tile.attendeeId] || 'Participant'}
                        </span>
                        <span className="block text-[9.5px] text-white/70 leading-tight">{roleFor(tile.attendeeId)}</span>
                      </span>
                      {mutedMap[tile.attendeeId] && <MicOff size={11} className="text-red-400 shrink-0" />}
                    </span>
                  </div>
                );
              })}
              {/* On group calls the local camera becomes a grid tile of its
                  own (the floating PiP hides — see below), so the layout
                  reads like one room with everyone in it, you included. */}
              {remoteTiles.length > 1 && (
                <div
                  className={`relative rounded-[14px] overflow-hidden bg-tertiary border transition-shadow ${
                    speakerIds[0] === myAttendeeId ? 'border-[#0DA7CA] ring-2 ring-[#0DA7CA]/50' : 'border-theme'}`}
                >
                  {localTile && cameraOn ? (
                    <video
                      ref={(el) => { if (el) videoElsRef.current[localTile.tileId] = el; }}
                      className="w-full h-full object-contain"
                      autoPlay
                      playsInline
                      muted
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <VideoOff size={20} className="text-muted-c" />
                    </div>
                  )}
                  <span className="absolute bottom-2 left-2 max-w-[85%] bg-black/55 rounded-lg px-2 py-1 flex items-center gap-1.5">
                    <span className="min-w-0">
                      <span className="block text-[11px] font-semibold text-white leading-tight truncate">{t.you}</span>
                      <span className="block text-[9.5px] text-white/70 leading-tight">{roleFor(myAttendeeId)}</span>
                    </span>
                    {muted && <MicOff size={11} className="text-red-400 shrink-0" />}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Local preview — sized to the camera's actual aspect ratio so a
              phone user sees the same vertical framing others receive.
              Top-right on phones so the floating captions card never covers
              it; bottom-right on desktop where the card doesn't exist.
              Hidden on group calls, where self is a grid tile instead. */}
          {(contentTile || remoteTiles.length <= 1) && (
          <div
            className="absolute top-3 right-3 md:top-auto md:bottom-5 md:right-5 rounded-[10px] overflow-hidden bg-black/80 border border-theme shadow-lg"
            style={localAspect < 1
              ? { height: pipPortrait, width: Math.round(pipPortrait * localAspect) }
              : { width: pipLandscape, height: Math.round(pipLandscape / localAspect) }}
          >
            {localTile && cameraOn ? (
              <video
                ref={(el) => {
                  if (!el) return;
                  videoElsRef.current[localTile.tileId] = el;
                  // 'resize' fires when the stream's dimensions change
                  // (e.g. the phone rotates); keep the box in step.
                  el.onloadedmetadata = el.onresize = () => {
                    if (el.videoWidth && el.videoHeight) {
                      setLocalAspect(el.videoWidth / el.videoHeight);
                    }
                  };
                }}
                className="w-full h-full object-cover scale-x-[-1]"
                autoPlay
                playsInline
                muted
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <VideoOff size={16} className="text-muted-c" />
              </div>
            )}
            <span className="absolute bottom-1 left-1.5 text-[10px] text-white/80">{t.you}</span>
          </div>
          )}
        </div>

        {/* Captions panel */}
        {(capsPanel === 'open' || (capsPanel === 'auto' && captionsOn)) && (
          <>
            {/* Desktop: side column with full history. */}
            <div className="hidden md:flex w-[300px] border-l border-theme bg-elevated flex-col">
              <div className="px-3 py-2.5 border-b border-theme flex items-center justify-between">
                <span className="text-[12px] font-semibold text-primary-c flex items-center gap-1.5">
                  <Captions size={14} /> {t.liveCaptions}
                </span>
                <div className="flex items-center gap-1.5">
                  <Languages size={13} className="text-accent-light shrink-0" />
                  <select
                    value={myLang}
                    onChange={(e) => setMyLang(e.target.value)}
                    title="Show captions in…"
                    className="text-[12px] font-semibold text-accent-light bg-tertiary border border-theme rounded-lg px-2 py-1 focus:outline-none focus:border-[#0DA7CA] cursor-pointer"
                  >
                    <option value="off">{t.noTranslate}</option>
                    {CAPTION_LANGS.map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div ref={captionsBoxRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2.5">
                {!captionsOn && (
                  <p className="text-[12px] text-muted-c pt-2">{t.capsWait}</p>
                )}
                {captionsOn && captions.length === 0 && (
                  <p className="text-[12px] text-muted-c pt-2">{t.captionsEmpty}</p>
                )}
                {captions.map((c) => (
                  <div key={c.id} className={c.partial ? 'opacity-60' : ''}>
                    <span className="text-[11px] font-semibold text-accent-light">{c.speaker}</span>
                    <p className="text-[12px] text-primary-c leading-snug">{c.text}</p>
                    {c.translated && (
                      <p className="text-[12px] text-muted-c italic leading-snug border-l-2 border-theme pl-2 mt-0.5">{c.translated}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Phones: floating live-captions card over the video —
                header row (status dot · language · settings · expand ·
                close) above the last few lines, sized/styled per the
                viewer's caption preferences. */}
            <div className="md:hidden absolute inset-x-2 bottom-2 z-20">
              <div className={`rounded-2xl overflow-hidden ${capPrefs.bg === 'solid' ? 'bg-black/80 backdrop-blur-sm' : ''}`}>
                <div className={`flex items-center gap-2 px-3 py-2 ${capPrefs.bg === 'solid' ? '' : 'bg-black/80 rounded-full'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                  <span className="text-[10px] font-bold tracking-[0.08em] text-white/90 shrink-0">{t.liveCaptions.toUpperCase()}</span>
                  <span className="text-[11px] text-white/60 truncate">
                    {myLang === 'off' ? t.noTranslate : (CAPTION_LANGS.find((l) => l.code === myLang)?.label || '')}
                  </span>
                  <span className="flex-1" />
                  <button onClick={() => setCapSettingsOpen(true)} className="w-7 h-7 rounded-full flex items-center justify-center text-white/80 bg-white/10 shrink-0">
                    <SlidersHorizontal size={13} />
                  </button>
                  <button onClick={() => setMobilePane('transcript')} className="w-7 h-7 rounded-full flex items-center justify-center text-white/80 bg-white/10 shrink-0">
                    <Maximize2 size={13} />
                  </button>
                  <button onClick={() => setCapsPanel('hidden')} className="w-7 h-7 rounded-full flex items-center justify-center text-white/80 bg-white/10 shrink-0">
                    <X size={13} />
                  </button>
                </div>
                <div className={`px-3 pb-2.5 pt-1 flex flex-col gap-1.5 ${capPrefs.bg === 'solid' ? '' : 'mt-1.5'}`}>
                  {!captionsOn && (
                    <p className={`${capPrefs.bg === 'highlight' ? 'bg-black/75 rounded-xl px-2.5 py-1.5 self-start' : ''} text-[12px] text-white/85`}>{t.capsWait}</p>
                  )}
                  {captionsOn && captions.length === 0 && (
                    <p className={`${capPrefs.bg === 'highlight' ? 'bg-black/75 rounded-xl px-2.5 py-1.5 self-start' : ''} text-[12px] text-white/85`}>{t.captionsEmpty}</p>
                  )}
                  {captionsOn && captions.slice(-3).map((c, i, arr) => (
                    <div
                      key={c.id}
                      className={`${capPrefs.bg === 'highlight' ? 'bg-black/75 rounded-xl px-2.5 py-1.5' : ''} ${
                        i < arr.length - 1 ? 'opacity-60' : ''} ${c.partial ? 'opacity-80' : ''}`}
                    >
                      {capPrefs.names && <span className="text-[10px] font-semibold text-[#6ED4EA]">{c.speaker}</span>}
                      <p className={`${CAP_SIZE[capPrefs.size]} text-white leading-snug`}>
                        {myLang === 'off' ? c.text : (c.translated || c.text)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile: full searchable transcript (audio/video keep running). */}
      {mobilePane === 'transcript' && (
        <div className="md:hidden absolute inset-0 z-30 bg-primary flex flex-col">
          <div className="flex items-center justify-between px-3 py-2.5">
            <button
              onClick={() => setMobilePane('call')}
              className="flex items-center gap-1.5 bg-tertiary border border-theme rounded-full px-3 py-1.5 text-[12px] font-semibold text-primary-c"
            >
              <Minimize2 size={13} /> {t.backToVideo}
            </button>
            <span className="text-[12px] font-mono text-muted-c tabular-nums">{fmtClock(elapsed)}</span>
          </div>
          <div className="px-4 pb-2 flex items-center gap-2">
            <h2 className="text-[18px] font-bold text-primary-c">{t.transcript}</h2>
            {captionsOn && (
              <span className="text-[10.5px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2 py-0.5 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live · {langLabel}
              </span>
            )}
            <span className="flex-1" />
            <button
              onClick={() => setCapSettingsOpen(true)}
              className="w-8 h-8 rounded-full bg-tertiary border border-theme flex items-center justify-center text-muted-c"
            >
              <SlidersHorizontal size={14} />
            </button>
          </div>
          <div className="px-4 pb-2">
            <div className="flex items-center gap-2 bg-tertiary border border-theme rounded-[10px] px-3 py-2">
              <Search size={13} className="text-muted-c shrink-0" />
              <input
                value={tSearch}
                onChange={(e) => setTSearch(e.target.value)}
                placeholder={t.searchVisit}
                className="flex-1 bg-transparent text-[13px] text-primary-c placeholder:text-muted-c focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
            {transcriptRows.length === 0 && (
              <p className="text-[12.5px] text-muted-c pt-3">
                {tq ? '—' : captionsOn ? t.captionsEmpty : t.capsWait}
              </p>
            )}
            {transcriptRows.map((c) => (
              <div key={c.id} className="flex gap-3">
                <span className="text-[11px] font-mono text-muted-c pt-0.5 shrink-0 w-[40px] tabular-nums">
                  {fmtClock((c.at || 0) - (callStartRef.current || c.at || 0))}
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-accent-light">{c.speaker}</div>
                  <p className="text-[13px] text-primary-c leading-snug">{c.text}</p>
                  {myLang !== 'off' && c.translated && (
                    <p className="text-[12.5px] text-muted-c italic leading-snug">{c.translated}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="px-4 py-3 text-[11px] text-muted-c border-t border-theme text-center">
            {isStaff ? t.autosaves : t.capsDisclaimer}
          </p>
        </div>
      )}

      {/* Caption settings bottom sheet */}
      {capSettingsOpen && (
        <div className="absolute inset-0 z-40" onClick={() => setCapSettingsOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0 md:inset-x-auto md:right-4 md:bottom-4 md:w-[380px] bg-elevated border border-theme rounded-t-2xl md:rounded-2xl p-5 pb-7 max-h-[85%] overflow-y-auto shadow-card"
          >
            <div className="w-10 h-1 rounded-full bg-tertiary mx-auto mb-3 md:hidden" />
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[17px] font-bold text-primary-c">{t.capSettings}</h3>
              <button onClick={() => setCapSettingsOpen(false)} className="text-[13px] font-semibold text-accent-light">{t.done}</button>
            </div>
            {lastFinal && (
              <div className="bg-black rounded-xl px-3.5 py-2.5 mb-4">
                {capPrefs.names && <div className="text-[10px] font-semibold text-[#6ED4EA]">{lastFinal.speaker}</div>}
                <p className={`${CAP_SIZE[capPrefs.size]} text-white leading-snug`}>
                  {myLang === 'off' ? lastFinal.text : (lastFinal.translated || lastFinal.text)}
                </p>
              </div>
            )}
            <div className="flex items-center justify-between py-1.5">
              <div className="pr-3">
                <div className="text-[13.5px] font-semibold text-primary-c">{t.liveCaptions}</div>
                <div className="text-[11.5px] text-muted-c">{isStaff ? t.liveCapsSub : (captionsOn ? t.liveCapsSub : t.capsWait)}</div>
              </div>
              <Toggle
                on={isStaff ? captionsOn : capsVisibleNow}
                busy={captionBusy}
                onChange={() => {
                  if (isStaff) toggleCaptions();
                  else setCapsPanel(capsVisibleNow ? 'hidden' : 'open');
                }}
              />
            </div>
            <div className="mt-3">
              <div className="text-[10.5px] font-bold tracking-[0.06em] text-muted-c uppercase mb-2">{t.textSize}</div>
              <div className="grid grid-cols-4 gap-1 bg-tertiary rounded-[10px] p-1">
                {['sm', 'md', 'lg', 'xl'].map((sz, i) => (
                  <button
                    key={sz}
                    onClick={() => setCapPrefs((pv) => ({ ...pv, size: sz }))}
                    className={`py-1.5 rounded-[8px] font-bold ${['text-[11px]', 'text-[13px]', 'text-[15px]', 'text-[18px]'][i]} ${
                      capPrefs.size === sz ? 'bg-elevated text-primary-c shadow-card' : 'text-muted-c'}`}
                  >
                    Aa
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10.5px] font-bold tracking-[0.06em] text-muted-c uppercase mb-2">{t.translateTo}</div>
              <select
                value={myLang}
                onChange={(e) => setMyLang(e.target.value)}
                className="w-full px-3 py-2.5 rounded-[10px] bg-tertiary border border-theme text-[13px] text-primary-c focus:outline-none focus:border-strong"
              >
                <option value="off">{t.noTranslate}</option>
                {CAPTION_LANGS.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <div className="text-[10.5px] font-bold tracking-[0.06em] text-muted-c uppercase mb-2">{t.background}</div>
              <div className="grid grid-cols-2 gap-2">
                {[['solid', t.solidDark], ['highlight', t.highlighted]].map(([v, lab]) => (
                  <button
                    key={v}
                    onClick={() => setCapPrefs((pv) => ({ ...pv, bg: v }))}
                    className={`py-2.5 rounded-[10px] text-[13px] font-semibold border transition-colors ${
                      capPrefs.bg === v ? 'border-strong text-primary-c bg-elevated' : 'border-theme text-muted-c bg-tertiary'}`}
                  >
                    {lab}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="text-[13.5px] font-semibold text-primary-c">{t.speakerNames}</div>
              <Toggle on={capPrefs.names} onChange={() => setCapPrefs((pv) => ({ ...pv, names: !pv.names }))} />
            </div>
          </div>
        </div>
      )}

      {/* Control bar */}
      <div className="flex items-start justify-center gap-3 px-4 py-3 border-t border-theme bg-elevated">
        {/* Speaker: a permanent, obvious way to force remote audio on.
            Mobile browsers block autoplayed audio in ways the automatic
            unlock can miss — this button rebinds and plays INSIDE the tap,
            which is the one context the browser always honors. */}
        <ControlBtn
          onClick={() => {
            const el = audioElRef.current;
            const av = meetingRef.current?.audioVideo;
            if (!el) return;
            try { if (av) av.bindAudioElement(el); } catch { /* keep going — play() may still work */ }
            el.muted = false;
            el.volume = 1;
            el.play().then(() => { setNeedsAudioTap(false); setAudioBlocked(false); }).catch(() => {});
          }}
          active={!audioBlocked}
          attention={audioBlocked}
          title={audioBlocked ? t.soundOff : t.soundOn}
          label={t.lblSound}
        >
          {audioBlocked ? <VolumeX size={17} /> : <Volume2 size={17} />}
        </ControlBtn>
        <ControlBtn onClick={toggleMute} active={!muted} title={muted ? 'Unmute' : 'Mute'} label={muted ? t.lblUnmute : t.lblMute}>
          {muted ? <MicOff size={17} /> : <Mic size={17} />}
        </ControlBtn>
        <ControlBtn onClick={toggleCamera} active={cameraOn} title={cameraOn ? 'Turn camera off' : 'Turn camera on'} label={t.lblCamera}>
          {cameraOn ? <Video size={17} /> : <VideoOff size={17} />}
        </ControlBtn>
        {canShare && (
          <ControlBtn onClick={toggleShare} active={!sharing} title={sharing ? 'Stop sharing your screen' : 'Share your screen'} label="Share">
            {sharing ? <ScreenShareOff size={17} /> : <ScreenShare size={17} />}
          </ControlBtn>
        )}
        {isStaff && (
          <ControlBtn onClick={toggleCaptions} active={captionsOn} title={captionsOn ? 'Turn off live captions' : 'Turn on live captions'} disabled={captionBusy} label={t.lblCaptions}>
            {captionBusy ? <Loader2 size={17} className="animate-spin" /> : <Captions size={17} />}
          </ControlBtn>
        )}
        {isStaff && (
          <ControlBtn onClick={() => setInviteOpen((v) => !v)} active title="Invite someone to this visit" label="Invite">
            <UserPlus size={17} />
          </ControlBtn>
        )}
        {isStaff && (
          // Opens the conversational MoCA flow in its own tab so the
          // clinician can administer a TBI assessment while sharing their
          // screen with the patient on this call.
          <ControlBtn
            onClick={() => window.open('/app/tbi-assessments/moca/new', '_blank', 'noopener')}
            active
            title="Open a TBI assessment in a new tab — share your screen so the patient follows along"
            label="TBI"
          >
            <Brain size={17} />
          </ControlBtn>
        )}
        {isStaff && (
          <ControlBtn
            onClick={toggleRecording}
            active={!recording}
            title={recording ? 'Stop recording' : 'Record this visit (make sure the patient has consented)'}
            disabled={recordingBusy}
            label="Record"
          >
            {recordingBusy ? <Loader2 size={17} className="animate-spin" /> : <Disc size={17} className={recording ? 'text-red-500' : ''} />}
          </ControlBtn>
        )}
        {!isStaff && (
          <ControlBtn
            onClick={() => setCapsPanel((v) =>
              (v === 'open' || (v === 'auto' && captionsOn)) ? 'hidden' : 'open')}
            active={capsPanel === 'open' || (capsPanel === 'auto' && captionsOn)}
            title={!captionsOn ? t.capsWait
              : (capsPanel === 'open' || capsPanel === 'auto') ? t.hideCaps : t.showCaps}
            label={t.lblCaptions}
          >
            <Captions size={17} />
          </ControlBtn>
        )}
        <div className="flex flex-col items-center gap-1 ml-2">
          <button
            onClick={hangUp}
            className="px-4 h-[42px] rounded-full bg-red-500 hover:bg-red-600 text-white text-[13px] font-semibold flex items-center gap-2 transition-colors"
            title={isStaff ? 'End the visit for everyone' : 'Leave the visit'}
          >
            <PhoneOff size={16} /> {isStaff ? 'End visit' : t.leave}
          </button>
          <span className="text-[9.5px] leading-none opacity-0 select-none">.</span>
        </div>
      </div>

      {inviteOpen && isStaff && (
        <InvitePanel
          orgId={currentOrg?.id}
          sessionId={sessionId}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}

// Mid-call invite: name + email/phone → the person gets a tokenized link
// (no account needed) and appears in the room when they click it. The link
// is also shown for copy-paste into any channel.
function InvitePanel({ orgId, sessionId, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState([]); // [{name, guest_url, copied}]

  const send = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSending(true); setError('');
    try {
      const { guests } = await api.addTelehealthGuests(orgId, sessionId, [{
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
      }]);
      setSent((prev) => [...prev, ...guests.map((g) => ({ ...g, copied: false }))]);
      setForm({ name: '', email: '', phone: '' });
    } catch (err) {
      setError(err.message || 'Could not send the invite');
    } finally {
      setSending(false);
    }
  };

  const copy = async (i) => {
    try {
      await navigator.clipboard.writeText(sent[i].guest_url);
      setSent((prev) => prev.map((g, j) => (j === i ? { ...g, copied: true } : g)));
      setTimeout(() => setSent((prev) => prev.map((g) => ({ ...g, copied: false }))), 2000);
    } catch { /* clipboard blocked — link is selectable */ }
  };

  const input = 'w-full px-3 py-[9px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[12px] focus:outline-none focus:border-[#0DA7CA]';

  return (
    <div className="absolute bottom-[74px] right-4 w-[320px] max-w-[calc(100vw-32px)] bg-elevated border border-theme rounded-[14px] shadow-pop p-4 z-30">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-bold text-primary-c flex items-center gap-1.5">
          <UserPlus size={14} className="text-accent-light" /> Invite to this visit
        </span>
        <button onClick={onClose} className="w-6 h-6 rounded-md bg-tertiary text-secondary-c flex items-center justify-center hover:bg-hover-surface">
          <X size={13} />
        </button>
      </div>
      {error && <p className="text-[11px] text-red-400 mb-2">{error}</p>}
      <div className="space-y-2">
        <input className={input} placeholder="Name *" value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
        <input className={input} type="email" placeholder="Email" value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <input className={input} placeholder="Phone" value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
      </div>
      <button
        onClick={send}
        disabled={sending || !form.name.trim()}
        className="w-full mt-3 py-[9px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 flex items-center justify-center gap-1.5"
      >
        {sending ? (<><Loader2 size={13} className="animate-spin" /> Sending…</>) : 'Send invite'}
      </button>
      {sent.length > 0 && (
        <div className="mt-3 space-y-2 max-h-[140px] overflow-y-auto">
          {sent.map((g, i) => (
            <div key={g.id || i} className="flex items-center gap-2 bg-tertiary border border-theme rounded-[10px] px-2.5 py-2">
              <span className="flex-1 min-w-0 text-[11px] text-primary-c truncate">
                {g.name} — invited{g.email || g.phone ? '' : ' (copy their link)'}
              </span>
              <button onClick={() => copy(i)} className="text-accent-light shrink-0" title="Copy their join link">
                {g.copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-c mt-2">
        No account needed — they join straight from their link.
      </p>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-elevated border border-theme rounded-[14px] p-8">{children}</div>
    </div>
  );
}

function ToggleChip({ active, onClick, onIcon, offIcon, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3.5 py-2 rounded-full text-[12px] font-medium flex items-center gap-1.5 border transition-colors ${
        active
          ? 'bg-accent-glow text-accent-light border-strong'
          : 'bg-tertiary text-muted-c border-theme'
      }`}
    >
      {active ? onIcon : offIcon} {label}
    </button>
  );
}

function ControlBtn({ onClick, active, title, disabled, attention, label, children }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`w-[42px] h-[42px] rounded-full flex items-center justify-center border transition-colors disabled:opacity-50 ${
          active
            ? 'bg-tertiary text-primary-c border-theme hover:bg-hover-surface'
            : 'bg-red-500/15 text-red-400 border-red-500/30'
        } ${attention ? 'animate-pulse ring-2 ring-red-400' : ''}`}
      >
        {children}
      </button>
      {label && <span className="text-[9.5px] font-medium text-muted-c leading-none">{label}</span>}
    </div>
  );
}

function Toggle({ on, onChange, busy }) {
  return (
    <button
      onClick={onChange}
      disabled={busy}
      className={`w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50 ${
        on ? 'bg-emerald-500' : 'bg-tertiary border border-theme'}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}
